import React, { useState } from 'react';

import { LucidTheme } from '@lucid/theme';

import Box from '../box/box';
import Link from '../link/link';
import { childrenComponents, PaginationChildren } from './pagination';

export interface ViewMorePaginationProps<T> {
  /** The children that are returned with the `Pagination` component. */
  children: PaginationChildren<T>;
  /** Allows the component to get additional styles. */
  className?: string;
  /** Checks whether or not the items should always be visible. */
  shouldAlwaysBeVisible?: boolean;
  /** The list of items to return with pagination. */
  items: T[];
  /** The number of items to be returned per each pagination page. `@default = 5` */
  itemsPerPage?: number;
  /** The text to be shown in the pagination link. */
  viewMoreText?: string;
};

const ViewMorePagination = <T, >(
  {
    shouldAlwaysBeVisible = false,
    children,
    className,
    items,
    itemsPerPage = 5,
    viewMoreText
  }: ViewMorePaginationProps<T>
) => {
  const [activePage, setActivePage] = useState(1);
  const totalPages = Math.ceil(items.length / itemsPerPage);
  const hasMore = activePage < totalPages;
  const visibleItemStart = (activePage - 1) * itemsPerPage;
  const visibleItemEnd = activePage * itemsPerPage;
  const visibleItems = items.slice(visibleItemStart, visibleItemEnd);
  let preChildren;

  if (shouldAlwaysBeVisible) {
    const preInvisibleItems = items.slice(0, visibleItemStart);
    if (preInvisibleItems.length > 0 && visibleItemStart !== 0) {
      preChildren = childrenComponents(children, { activePage, totalPages, visibleItems: preInvisibleItems });
    }
  }

  return (
    <Box
      css={{
        label: 'viewMorePagination',
        width: '100%'
      }}
      // eslint-disable-next-line react/jsx-sort-props
      className={className}
    >
      {preChildren && (
        <Box
          css={(theme: LucidTheme) => [
            {
              label: 'preChildren',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              justifyContent: 'space-between',
              position: 'relative',
              padding: `${theme.space[6]}px 0`,
              alignItems: 'center',
              width: '100%',
              display: 'none',
              visibility: 'hidden',
              '& > a': {
                alignSelf: 'stretch'
              }
            },
            shouldAlwaysBeVisible && {
              display: 'flex',
              visibility: 'visible'
            }
          ]}
          // eslint-disable-next-line react/jsx-sort-props
          className={className}
        >
          {preChildren}
        </Box>
      )}
      {childrenComponents(children, { activePage, totalPages, visibleItems })}
      {hasMore && (
        <Box
          css={(theme: LucidTheme) => ({
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            justifyContent: 'space-between',
            position: 'relative',
            padding: `${theme.space[6]}px 0`,
            alignItems: 'center',
            width: '100%'
          })}
        >
          <hr
            css={(theme: LucidTheme) => ({
              border: 0,
              borderBottom: `2px solid ${theme.colors.coolGrey[10]}`,
              margin: 0,
              width: '100%'
            })}
          />
          <Link
            hasArrow
            css={(theme: LucidTheme) => ({
              cursor: 'pointer',
              padding: theme.space[2],
              whiteSpace: 'nowrap',
              background: 'transparent',
              border: 0,
              fontSize: theme.fontSizes[3],
              color: theme.colors.secondary,
              '&:visited': {
                color: theme.colors.secondary
              },
              '&:hover': {
                color: theme.colors.secondary
              }
            })}
            onClick={(e) => {
              e.preventDefault();
              setActivePage(activePage + 1);
            }}
          >
            {viewMoreText}
          </Link>
        </Box>
      )}
    </Box>
  );
};

export { ViewMorePagination };
export default ViewMorePagination;
